import { z } from 'zod';

/**
 * Lossless, model-facing persistence format for agent conversation turns.
 *
 * Each assistant `ChatMessage` row stores every LangChain message produced
 * during its turn (AI messages with tool calls, tool results, the final
 * answer) in LangChain's stable `StoredMessage` shape. User rows store the
 * single human message. Rebuilding model history is a pure
 * `payload → messages` mapping — no heuristics, no reconstruction.
 *
 * The schema mirrors `StoredMessage` from `@langchain/core` without importing
 * it: schema is bundled into frontends that must not depend on LangChain.
 * `content` remains unknown because LangChain's runtime codec preserves
 * provider content blocks even though its current `StoredMessage` declaration
 * still narrows this field to string.
 */
export const storedAgentMessageSchema = z.object({
  type: z.string().min(1),
  data: z
    .object({
      content: z.unknown(),
      role: z.string().optional(),
      name: z.string().optional(),
      tool_call_id: z.string().optional(),
      additional_kwargs: z.record(z.unknown()).optional(),
      response_metadata: z.record(z.unknown()).optional(),
      id: z.string().optional(),
    })
    .passthrough(),
});

export type StoredAgentMessage = z.infer<typeof storedAgentMessageSchema>;

export const AGENT_MESSAGE_PAYLOAD_VERSION = 1 as const;

export const agentMessagePayloadSchema = z.object({
  v: z.literal(AGENT_MESSAGE_PAYLOAD_VERSION),
  messages: z.array(storedAgentMessageSchema),
});

export type AgentMessagePayload = z.infer<typeof agentMessagePayloadSchema>;
